import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { type Task, task } from 'ember-concurrency';
import type MarketSettingsPropertyTagsOrganizeController from 'lumen/controllers/market/settings/property-tags/organize';
import type { PropertyTags, ReservationFilter } from 'lumen/models';
import type CustomTagDefinition from 'lumen/models/custom-tag-definition';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import {
  setupUnsavedChangesTransitionHandler,
  type TransitionHandler,
} from 'lumen/utils/unsaved-changes-transition-handlers';

interface PropertyTagsRouteData {
  definitions: CustomTagDefinition[];
  propertyTags: PropertyTags[];
  reservationFilters: ReservationFilter[];
}

interface PropertyTagsRouteModel {
  data: Task<PropertyTagsRouteData, []>;
}

export default class MarketSettingsPropertyTagsOrganizeRoute extends Route {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  #transitionHandler?: TransitionHandler;

  requestDataTask = task({ restartable: true }, async () => {
    const marketId = this.hotelAndMarketService.market.id;

    // Values arrive embedded in property-tags, so the query below repopulates
    // them. Drop the old set first: a value whose definition was deleted
    // elsewhere is no longer returned by the server and would linger with a
    // dangling definition reference.
    this.store.unloadAll('custom-tag-value');

    try {
      const [propertyTags, definitions, reservationFilters] = await Promise.all(
        [
          this.store.query<PropertyTags>('property-tags', {
            namespace: APIGATEWAY,
            market_id: marketId,
          }),
          this.store.query<CustomTagDefinition>('custom-tag-definition', {
            market_id: marketId,
          }),
          // Filters are an enhancement to the property tags table, not core
          // to it — a failure here shouldn't block the page from rendering.
          this.store
            .query<ReservationFilter>('reservation-filter', {})
            .catch((error: unknown): ReservationFilter[] => {
              this.errorTracking.captureException(error);
              return [];
            }),
        ],
      );

      return { propertyTags, definitions, reservationFilters };
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.alerting.showErrorAlert({
        text: getAPIErrorMessage(error, this.intl),
      });
      this.errorTracking.captureException(error);

      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model() {
    const model: PropertyTagsRouteModel = {
      data: this.requestDataTask,
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    model.data.perform();

    return model;
  }

  resetController(
    controller: MarketSettingsPropertyTagsOrganizeController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (isExiting && this.#transitionHandler) {
      this.#transitionHandler.cleanup();
      this.#transitionHandler = undefined;
    }
  }

  setupController(
    controller: MarketSettingsPropertyTagsOrganizeController,
    model: unknown,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    this.#transitionHandler = setupUnsavedChangesTransitionHandler(
      this.router,
      controller,
      'market.settings.property-tags.organize',
    );
  }
}
